import { useState } from 'react'
import { useMe } from '@/auth/useMe'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { createTask } from '@/features/preinvest/tasks/api'
import { toast } from '@/lib/toast'

/** Adds `days` to today and returns YYYY-MM-DD (local). */
function inDays(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date.toLocaleDateString('sv-SE')
}

const QUICK_PICKS = [
  { label: 'Morgen', days: 1 },
  { label: 'Volgende week', days: 7 },
  { label: 'Over een maand', days: 30 },
] as const

/**
 * Plant een opvolgtaak voor een contact (spec-47): quick-picks of vrije datum,
 * optionele notitie als taaktitel. Maakt via de bestaande tasks-api een taak
 * met `context: 'relaties'` aan, toegewezen aan de ingelogde gebruiker.
 */
export function FollowUpDialog({
  open,
  onOpenChange,
  personId,
  personName,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  personId: string
  personName: string
  onCreated?: () => void
}) {
  const { me } = useMe()
  const [dueDate, setDueDate] = useState(inDays(7))
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit() {
    if (busy) return
    setBusy(true)
    try {
      await createTask({
        title: note.trim() || `Opvolgen: ${personName}`,
        priority: 'normal',
        due_date: dueDate || null,
        assignee_ids: me ? [me.id] : [],
        context: 'relaties',
        person_id: personId,
      })
      toast.success('Opvolgtaak aangemaakt.')
      setNote('')
      onOpenChange(false)
      onCreated?.()
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Opvolgtaak aanmaken mislukt.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Opvolgen: {personName}</DialogTitle>
          <DialogDescription>Plan een opvolgtaak voor dit contact.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {QUICK_PICKS.map((pick) => (
            <Button
              key={pick.label}
              type="button"
              size="sm"
              variant={dueDate === inDays(pick.days) ? 'default' : 'outline'}
              onClick={() => setDueDate(inDays(pick.days))}
            >
              {pick.label}
            </Button>
          ))}
          <Input
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
            aria-label="Opvolgdatum"
            className="w-40"
          />
        </div>
        <Input
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Notitie (wordt de taaktitel)…"
          aria-label="Notitie"
        />
        <Button type="button" onClick={() => void submit()} disabled={busy || !me}>
          {busy ? 'Bezig…' : 'Opvolgtaak aanmaken'}
        </Button>
      </DialogContent>
    </Dialog>
  )
}
